// The `/org/{org_ref}/crm/*` operations: the thirteen authored at backend e9c12fdd6, and CRM-5's
// nine (the whole list and the segment family). The committed OpenAPI snapshot deliberately
// carries no `/org` family, so these calls use the configured `givmoClient` escape hatch and inherit
// the ordinary auth/App Check/error middleware.

import { givmoClient, isGivmoError, type Options } from '@givmo/api-client';
import type {
  CrmImportedContactCreateRequest,
  CrmImportedContactUpdateRequest,
  CrmNoteRequest,
  CrmSegmentCreateRequest,
  CrmSegmentEvaluateRequest,
  CrmSegmentUpdateRequest,
  CrmTagAddRequest,
} from './wire-types';

export const CRM_TRANSPORT_OPERATION_COUNT = 23;
export const CRM_CSV_RESPONSE_OPTIONS = {
  parseAs: 'blob',
  responseStyle: 'fields',
  throwOnError: true,
} as const satisfies Pick<Options, 'parseAs' | 'responseStyle' | 'throwOnError'>;

function base(orgRef: string): string {
  return `/org/${encodeURIComponent(orgRef)}/crm`;
}

function donorBase(orgRef: string, donorId: string): string {
  return `${base(orgRef)}/donors/${encodeURIComponent(donorId)}`;
}

function suppliedBase(orgRef: string): string {
  return `${base(orgRef)}/imported-contacts`;
}

export function isCrmUnavailable(error: unknown): boolean {
  return isGivmoError(error) && error.status === 404;
}

export async function fetchCrmDonors(
  orgRef: string,
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: `${base(orgRef)}/donors`,
    signal: options.signal,
    throwOnError: true,
  });
  return data;
}

export async function exportCrmDonorsCsv(orgRef: string) {
  return givmoClient.get({
    url: `${base(orgRef)}/donors.csv`,
    ...CRM_CSV_RESPONSE_OPTIONS,
  });
}

export async function fetchCrmDonor(
  orgRef: string,
  donorId: string,
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: donorBase(orgRef, donorId),
    signal: options.signal,
    throwOnError: true,
  });
  return data;
}

export async function addCrmDonorTag(
  orgRef: string,
  donorId: string,
  body: CrmTagAddRequest,
): Promise<unknown> {
  const { data } = await givmoClient.put({
    url: `${donorBase(orgRef, donorId)}/tags`,
    body,
    throwOnError: true,
  });
  return data;
}

export async function removeCrmDonorTag(
  orgRef: string,
  donorId: string,
  label: string,
): Promise<void> {
  await givmoClient.delete({
    url: `${donorBase(orgRef, donorId)}/tags/${encodeURIComponent(label)}`,
    throwOnError: true,
  });
}

export async function createCrmDonorNote(
  orgRef: string,
  donorId: string,
  body: CrmNoteRequest,
): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: `${donorBase(orgRef, donorId)}/notes`,
    body,
    throwOnError: true,
  });
  return data;
}

export async function updateCrmDonorNote(
  orgRef: string,
  donorId: string,
  noteId: string,
  body: CrmNoteRequest,
): Promise<unknown> {
  const { data } = await givmoClient.patch({
    url: `${donorBase(orgRef, donorId)}/notes/${encodeURIComponent(noteId)}`,
    body,
    throwOnError: true,
  });
  return data;
}

export async function deleteCrmDonorNote(
  orgRef: string,
  donorId: string,
  noteId: string,
): Promise<void> {
  await givmoClient.delete({
    url: `${donorBase(orgRef, donorId)}/notes/${encodeURIComponent(noteId)}`,
    throwOnError: true,
  });
}

export async function fetchCrmImportedContacts(
  orgRef: string,
  read: { q?: string; limit: number; offset: number; signal?: AbortSignal },
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: suppliedBase(orgRef),
    query: {
      ...(read.q === undefined || read.q.length === 0 ? {} : { q: read.q }),
      limit: read.limit,
      offset: read.offset,
    },
    signal: read.signal,
    throwOnError: true,
  });
  return data;
}

export async function exportCrmImportedContactsCsv(orgRef: string) {
  return givmoClient.get({
    url: `${suppliedBase(orgRef)}.csv`,
    ...CRM_CSV_RESPONSE_OPTIONS,
  });
}

export async function createCrmImportedContact(
  orgRef: string,
  body: CrmImportedContactCreateRequest,
): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: suppliedBase(orgRef),
    body,
    throwOnError: true,
  });
  return data;
}

/** One of the organization's own contacts, with why it may not be emailed (the contact panel). */
export async function fetchCrmImportedContact(
  orgRef: string,
  contactId: string,
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: `${suppliedBase(orgRef)}/${encodeURIComponent(contactId)}`,
    signal: options.signal,
    throwOnError: true,
  });
  return data;
}

export async function updateCrmImportedContact(
  orgRef: string,
  contactId: string,
  body: CrmImportedContactUpdateRequest,
): Promise<unknown> {
  const { data } = await givmoClient.patch({
    url: `${suppliedBase(orgRef)}/${encodeURIComponent(contactId)}`,
    body,
    throwOnError: true,
  });
  return data;
}

export async function deleteCrmImportedContact(orgRef: string, contactId: string): Promise<void> {
  await givmoClient.delete({
    url: `${suppliedBase(orgRef)}/${encodeURIComponent(contactId)}`,
    throwOnError: true,
  });
}

// ── CRM-5: the whole list and segments ────────────────────────────────────────────────────────────

function segmentsBase(orgRef: string): string {
  return `${base(orgRef)}/segments`;
}

function segmentBase(orgRef: string, segmentId: string): string {
  return `${segmentsBase(orgRef)}/${encodeURIComponent(segmentId)}`;
}

export async function fetchCrmContacts(
  orgRef: string,
  read: { q?: string; limit: number; offset: number; signal?: AbortSignal },
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: `${base(orgRef)}/contacts`,
    query: {
      ...(read.q === undefined || read.q.length === 0 ? {} : { q: read.q }),
      limit: read.limit,
      offset: read.offset,
    },
    signal: read.signal,
    throwOnError: true,
  });
  return data;
}

export async function fetchCrmSegments(
  orgRef: string,
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: segmentsBase(orgRef),
    signal: options.signal,
    throwOnError: true,
  });
  return data;
}

export async function fetchCrmSegmentOptions(
  orgRef: string,
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: `${segmentsBase(orgRef)}/options`,
    signal: options.signal,
    throwOnError: true,
  });
  return data;
}

export async function createCrmSegment(
  orgRef: string,
  body: CrmSegmentCreateRequest,
): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: segmentsBase(orgRef),
    body,
    throwOnError: true,
  });
  return data;
}

/** An unsaved definition's members now: a page, and the total. The builder's live count. */
export async function evaluateCrmSegment(
  orgRef: string,
  body: CrmSegmentEvaluateRequest,
  read: { limit: number; offset: number; signal?: AbortSignal },
): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: `${segmentsBase(orgRef)}/evaluate`,
    body,
    query: { limit: read.limit, offset: read.offset },
    signal: read.signal,
    throwOnError: true,
  });
  return data;
}

export async function fetchCrmSegment(
  orgRef: string,
  segmentId: string,
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: segmentBase(orgRef, segmentId),
    signal: options.signal,
    throwOnError: true,
  });
  return data;
}

export async function updateCrmSegment(
  orgRef: string,
  segmentId: string,
  body: CrmSegmentUpdateRequest,
): Promise<unknown> {
  const { data } = await givmoClient.patch({
    url: segmentBase(orgRef, segmentId),
    body,
    throwOnError: true,
  });
  return data;
}

export async function deleteCrmSegment(orgRef: string, segmentId: string): Promise<void> {
  await givmoClient.delete({
    url: segmentBase(orgRef, segmentId),
    throwOnError: true,
  });
}

export async function fetchCrmSegmentMembers(
  orgRef: string,
  segmentId: string,
  read: { limit: number; offset: number; signal?: AbortSignal },
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: `${segmentBase(orgRef, segmentId)}/members`,
    query: { limit: read.limit, offset: read.offset },
    signal: read.signal,
    throwOnError: true,
  });
  return data;
}
